/**
 * Coach offer server functions: the main offer, offer lists, mini sprints,
 * deletion, summit offer statuses, and opening a summit special.
 *
 * Aliases: coach offers, getCoachOffer, summit offer status, mini sprints,
 * open summit special
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import { OfferType } from "~/lib/schemas/db-enums";
import type { SummitSpecialStatus } from "~/lib/shared/summit-special";
import { UserFacingError } from "~/lib/shared/user-facing-error";

const DeleteOfferSchema = z.object({
  offerId: z.string(),
});

const OpenSummitSpecialSchema = z.object({
  summitId: z.string().uuid(),
});

/** The signed-in coach's primary offer, for the offers list's main slot. */
export const getCoachOffer = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }) => {
    const { loadOfferEditData } = await import("~/lib/server/offer-edit-data");
    return loadOfferEditData(context.coach.id);
  });

/** All of the coach's standard offers; the list decides which one fills the main slot. */
export const getCoachOffers = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }) => {
    const { getOffersByCoachId } = await import("~/lib/server/coach");
    const offers = await getOffersByCoachId(
      context.coach.id,
      OfferType.Standard,
    );
    return { offers };
  });

export const createNewOffer = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }) => {
    const coach = context.coach;
    const { getDBWithInit } = await import("~/lib/db");
    const { offers } = await import("~/../drizzle/schema");
    const offer = await (
      await getDBWithInit()
    )
      .insert(offers)
      .values({
        coachId: coach.id,
        headline: "New Offer",
      })
      .returning();

    return { offerId: offer[0].id };
  });

export const createNewMiniSprint = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }) => {
    const coach = context.coach;
    const { createOffer } = await import("~/lib/server/coach");
    const offer = await createOffer({
      coachId: coach.id,
      type: OfferType.MiniSprint,
      preHeadline: "Free Mini Sprint",
      headline: "Your 5-Day Mini Sprint",
      description: "Even if you've felt stuck for months.",
      durationLabel: "Day",
    });
    return { offerId: offer.id };
  });

export const getCoachMiniSprints = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }) => {
    const { getOffersByCoachId } = await import("~/lib/server/coach");
    const offers = await getOffersByCoachId(
      context.coach.id,
      OfferType.MiniSprint,
    );
    return { offers };
  });

export const deleteOffer = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(DeleteOfferSchema)
  .handler(async ({ data, context }) => {
    const { deleteOffer } = await import("~/lib/server/offer-lifecycle");
    await deleteOffer({ coachId: context.coach.id, offerId: data.offerId });
    return { success: true };
  });

export const SUMMIT_OFFER_STATUS_QUERY_KEY = [
  "coach",
  "summit-offers",
  "statuses",
] as const;

export const getSummitOfferStatuses = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }): Promise<SummitSpecialStatus[]> => {
    const { listSummitSpecialStatuses } =
      await import("~/lib/server/summit-special-offer");
    return listSummitSpecialStatuses(context.coach.id);
  });

/**
 * Open the coach's summit special for a summit, creating it on first use, so
 * the offer editor can load it. Refuses a summit the coach cannot sell into.
 */
export const openSummitSpecial = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(OpenSummitSpecialSchema)
  .handler(async ({ data, context }): Promise<{ offerId: string }> => {
    const { openSummitSpecialOffer } =
      await import("~/lib/server/summit-special-offer");
    const opened = await openSummitSpecialOffer({
      coachId: context.coach.id,
      summitId: data.summitId,
      summitSessionId: null,
      now: new Date(),
    });
    if (!opened) {
      throw new UserFacingError(
        "You can't create a summit special for this summit.",
      );
    }
    return opened;
  });

export const resolveStatusLabel = (status: SummitSpecialStatus | undefined) => {
  if (!status) {
    return {
      label: "Not started",
      variant: "gray" as const,
      className: undefined,
    };
  }

  if (status.isLive) {
    return { label: "Live", variant: "success" as const, className: undefined };
  }

  return {
    label: "Draft",
    variant: "outline" as const,
    className: "border-amber-200 bg-amber-50 text-amber-800",
  };
};
